import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { Typography, Box, CircularProgress, Alert, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Chip } from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import PeopleIcon from '@mui/icons-material/People';
import HelpIcon from '@mui/icons-material/Help';
import { useRoleAccess } from '../hooks/useRoleAccess';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { parseLocalDate } from '../utils/dateHelpers';

export default function Projects() {
  const navigate = useNavigate();
  const { canManageProjects, canDeleteProjects, isAdmin, isServiceManager, isTeamLeader, isJRServiceManager } = useRoleAccess();
  const { userId } = useCurrentUser();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });

  // Stato per i customers (per il dropdown)
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);

  // Stato per il dialog di create/update
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    description: '',
    customer_id: '',
    start_date: '',
    end_date: '',
    active: true,
    project_code: '',
    days_after_block: 1,
    days_before_block: 1,
    ticket_id_prefix: '',
    regex: '',
    regex_desc: '',
    previous_month_blocked: false,
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [regexHelpOpen, setRegexHelpOpen] = useState(false);
  
  // Stato per tenere traccia dei progetti con worklog associati
  const [projectsWithWorklogs, setProjectsWithWorklogs] = useState(new Set());

  // Definizione delle colonne per la DataGrid
  const columns = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {/* <Tooltip title="Manage Project">
            <IconButton size="small" onClick={() => navigate(`/projects/${params.row.id}`)}>
              <ManageAccountsIcon />
            </IconButton>
          </Tooltip> */}

          {canManageProjects() && (
            <Tooltip title="Edit Project">
              <IconButton size="small" onClick={() => navigate(`/projects/${params.row.id}`)}>
                <EditIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDeleteProjects() && (
            projectsWithWorklogs.has(params.row.id) ? (
              <Tooltip title="Cannot delete: Project has associated worklogs">
                <span>
                  <IconButton size="small" disabled sx={{ color: 'grey.400' }}>
                    <DeleteIcon />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => handleDeleteProject(params.row.id)}>
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )
          )}
        </Box>
      ),
    },
    { 
      field: 'active', 
      headerName: 'Status', 
      width: 70,
      renderCell: (params) => (
        <Box 
          sx={{
            width: 12,
            height: 12,
            borderRadius: '50%',
            backgroundColor: params.value ? '#4caf50' : '#bdbdbd',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '0 auto'
          }}
          title={params.value ? 'Active' : 'Inactive'}
        />
      )
    },
    { field: 'name', headerName: 'Name', width: 200 },
    { field: 'project_code', headerName: 'Code', width: 120 },
    { field: 'id', headerName: 'ID', width: 80, hide: true },
    { field: 'ticket_id_prefix', headerName: 'Ticket ID Prefix', width: 150 },
    { field: 'regex', headerName: 'Regex Pattern', width: 200 },
    { field: 'regex_desc', headerName: 'Regex Description', width: 200 },
    { 
      field: 'customer_name', 
      headerName: 'Customer', 
      width: 150,
      valueGetter: (params) => {
        return params.row.customer?.name || 'N/A';
      }
    },
    { 
      field: 'worklog_restrictions', 
      headerName: 'Worklog Restrictions', 
      width: 250,
      sortable: false,
      renderCell: (params) => {
        // Debug: log dei valori per verificare cosa arriva dal backend
        console.log('Worklog Restrictions Debug:', {
          project_id: params.row.id,
          project_name: params.row.name,
          days_before_block: params.row.days_before_block,
          days_after_block: params.row.days_after_block,
          previous_month_blocked: params.row.previous_month_blocked
        });
        
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Prima riga: days before e after */}
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Chip 
                label={`${params.row.days_before_block || 1}d before`} 
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: '18px' }}
              />
              <Chip 
                label={`${params.row.days_after_block || 1}d after`} 
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.65rem', height: '18px' }}
              />
            </Box>
            {/* Seconda riga: previous month (sempre visibile) */}
                 <Chip
                   label={`Prev month: ${params.row.previous_month_blocked ? 'blocked' : 'allowed'}`}
                   size="small"
                   variant="outlined"
                   color={params.row.previous_month_blocked ? "warning" : "success"}
                   sx={{
                     fontSize: '0.65rem',
                     height: '18px',
                     borderWidth: '1px',
                     color: 'black',
                     '& .MuiChip-label': {
                       color: 'black'
                     }
                   }}
                 />
          </Box>
        );
      }
    },
    { 
      field: 'start_date', 
      headerName: 'Start Date', 
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '';
        // Fix timezone: parseLocalDate interpreta la data come locale
        const date = parseLocalDate(params.value);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleDateString('en-GB');
      }
    },
    { 
      field: 'end_date', 
      headerName: 'End Date', 
      width: 120,
      valueFormatter: (params) => {
        if (!params.value) return '';
        // Fix timezone: parseLocalDate interpreta la data come locale
        const date = parseLocalDate(params.value);
        if (isNaN(date.getTime())) return '';
        // Gestisce date "infinite" (anno 9999)
        if (date.getFullYear() >= 9999) return 'No End Date';
        return date.toLocaleDateString('en-GB');
      }
    },
    { field: 'description', headerName: 'Description', width: 250 },
    { 
      field: 'created_at', 
      headerName: 'Created At', 
      width: 140,
      valueFormatter: (params) => {
        if (!params.value) return '';
        // Fix timezone: parseLocalDate interpreta datetime come locale
        const date = parseLocalDate(params.value);
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    },
    { 
      field: 'updated_at', 
      headerName: 'Updated At', 
      width: 140,
      valueFormatter: (params) => {
        if (!params.value) return '';
        // Fix timezone: parseLocalDate interpreta datetime come locale
        const date = parseLocalDate(params.value);
        return date.toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      }
    },
  ];

  // Fetch projects
  const fetchProjects = async () => {
    setLoading(true);
    setError(null);
    try {
      let projects = [];
      
      if (isAdmin) {
        // Admin vede tutti i progetti
        console.log('[PROJECTS] Admin user - fetching all projects');
        const response = await fetch(API_ENDPOINTS.PROJECTS);
        console.log('[PROJECTS] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('[PROJECTS] Error response:', errorText);
          throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
        }
        
        const data = await response.json();
        console.log('[PROJECTS] Response data:', data);
        projects = data.projects || [];
      } else if (isServiceManager || isTeamLeader || isJRServiceManager) {
        // Service Manager, Team Leader, JR Service Manager vedono solo progetti con ruoli 1,2,3
        console.log('[PROJECTS] Fetching project users for userId:', userId);
        const response = await fetch(`${API_ENDPOINTS.PROJECT_USERS}?user_id=${userId}`);
        console.log('[PROJECTS] Project users API response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('[PROJECTS] Error response:', errorText);
          throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
        }
        
        const data = await response.json();
        console.log('[PROJECTS] Project users API response:', data);
        
        if (Array.isArray(data.user_projects_with_references)) {
          const currentDate = new Date();
          
          // Filtra per project_role_id in [1,2,3] e validity_end_date
          const validProjectUsers = data.user_projects_with_references.filter(item => {
            const projectRoleId = item.project_role?.id;
            const isValidRole = [1, 2, 3].includes(projectRoleId);
            
            // Se validity_end_date è vuota, è sempre valida per il filtro progetti
            if (!item.validity_end_date) {
              console.log('[PROJECTS] User without validity_end_date (always valid):', item);
              return isValidRole;
            }
            
            // Fix timezone: parseLocalDate interpreta la data come locale
            const endDate = parseLocalDate(item.validity_end_date);
            const isValidDate = endDate > currentDate;
            
            console.log('[PROJECTS] Filter check for project:', {
              project_id: item.project?.id,
              project_name: item.project?.name,
              validity_end_date: item.validity_end_date,
              endDate,
              currentDate,
              isValidDate,
              project_role_id: projectRoleId,
              isValidRole,
              final: isValidDate && isValidRole
            });
            
            return isValidDate && isValidRole;
          });
          console.log('[PROJECTS] Valid project users after filter:', validProjectUsers);
          
          // Estrai i progetti direttamente dalla risposta
          projects = validProjectUsers.map(item => ({
            id: item.project.id,
            name: item.project.name,
            description: item.project.description,
            active: item.project.active,
            project_code: item.project.project_code,
            customer_id: item.project.customer_id,
            start_date: item.project.start_date,
            end_date: item.project.end_date,
            days_after_block: item.project.days_after_block,
            days_before_block: item.project.days_before_block,
            ticket_id_prefix: item.project.ticket_id_prefix,
            regex: item.project.regex,
            regex_desc: item.project.regex_desc,
            previous_month_blocked: item.project.previous_month_blocked,
            customer: item.customer
          }));
          
          console.log('[PROJECTS] Final projects set:', projects);
        }
      } else {
        // Altri ruoli usano la logica originale
        console.log('[PROJECTS] Other roles - fetching from:', API_ENDPOINTS.PROJECTS);
        const response = await fetch(API_ENDPOINTS.PROJECTS);
        console.log('[PROJECTS] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('[PROJECTS] Error response:', errorText);
          throw new Error(`API Error ${response.status}: ${errorText || 'Unknown error'}`);
        }
        
        const data = await response.json();
        console.log('[PROJECTS] Response data:', data);
        projects = data.projects || [];
      }
      
      console.log('[PROJECTS] Projects loaded:', projects.length, 'items');
      setRows(projects.map((row, idx) => ({ id: row.id || idx, ...row })));
    } catch (err) {
      console.error('[PROJECTS] Fetch error:', err);
      setError(`Error loading projects: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch customers for dropdown
  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.CUSTOMERS);
      if (!response.ok) throw new Error("Error fetching customers");
      const data = await response.json();
      const customersList = data.customers || [];
      console.log('Customers loaded for dropdown:', customersList);
      setCustomers(customersList);
    } catch (err) {
      console.error('Error loading customers:', err);
      setCustomers([]);
    } finally {
      setCustomersLoading(false);
    }
  };

  // Funzione per controllare se un progetto ha worklog associati
  const checkProjectsWithWorklogs = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.WORKLOGS);
      if (!response.ok) throw new Error("Error fetching worklogs");
      const data = await response.json();
      const worklogs = data.worklogs || [];
      
      // Estrai gli ID dei progetti che hanno worklog associati
      const projectIdsWithWorklogs = new Set(
        worklogs.map(worklog => worklog.project_id).filter(id => id)
      );
      
      setProjectsWithWorklogs(projectIdsWithWorklogs);
    } catch (err) {
      console.error('Error checking projects with worklogs:', err);
      setProjectsWithWorklogs(new Set());
    }
  };

  useEffect(() => {
    fetchProjects();
    fetchCustomers();
    checkProjectsWithWorklogs();
  }, [isAdmin, isServiceManager, isTeamLeader, isJRServiceManager, userId]);

  // Elimina project
  const handleDeleteProject = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) return;
    
    try {
      const response = await fetch(`${API_ENDPOINTS.PROJECTS}?id=${id}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        const err = await response.text();
        throw new Error(err);
      }
      
      // Ricarica la lista
      fetchProjects();
    } catch (err) {
      alert(`Error deleting project: ${err.message}`);
    }
  };

  const handleOpenDialog = () => {
    setFormData({
      id: '',
      name: '',
      description: '',
      customer_id: '',
      start_date: '',
      end_date: '',
      active: true,
      project_code: '',
      days_after_block: 1,
      days_before_block: 1,
      ticket_id_prefix: '',
      regex: '',
      regex_desc: '',
      previous_month_blocked: false,
    });
    setEditingRow(null);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormError(null);
  };

  const handleEditDialog = (row) => {
    // Gestione delle date con controllo per date "infinite"
    const getFormattedDate = (dateStr) => {
      if (!dateStr) return '';
      // Se la data è già in formato YYYY-MM-DD, verifica direttamente l'anno
      if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const year = parseInt(dateStr.split('-')[0], 10);
        // Se è una data "infinita" (anno 9999), non la mostriamo nel form
        if (year >= 9999) return '';
        return dateStr;
      }
      // Se la data contiene 'T' (formato ISO con orario), estrai solo la parte data
      if (typeof dateStr === 'string' && dateStr.includes('T')) {
        const datePart = dateStr.split('T')[0];
        const year = parseInt(datePart.split('-')[0], 10);
        if (year >= 9999) return '';
        return datePart;
      }
      // Fallback: converti tramite Date object (fix timezone)
      const date = parseLocalDate(dateStr);
      if (isNaN(date.getTime())) return '';
      // Se è una data "infinita" (anno 9999), non la mostriamo nel form
      if (date.getFullYear() >= 9999) return '';
      // Usa le componenti locali per evitare shift di timezone
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setFormData({
      id: row.id || '',
      name: row.name || '',
      description: row.description || '',
      customer_id: row.customer?.id || '',
      start_date: getFormattedDate(row.start_date),
      end_date: getFormattedDate(row.end_date),
      active: row.active !== undefined ? row.active : true,
      project_code: row.project_code || '',
      days_after_block: row.days_after_block || 1,
      days_before_block: row.days_before_block || 1,
      ticket_id_prefix: row.ticket_id_prefix || '',
      regex: row.regex || '',
      regex_desc: row.regex_desc || '',
      previous_month_blocked: row.previous_month_blocked || false,
    });
    setEditingRow(row);
    setFormError(null);
    setOpenDialog(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleRegexHelpOpen = () => {
    setRegexHelpOpen(true);
  };

  const handleRegexHelpClose = () => {
    setRegexHelpOpen(false);
  };

  // Salva project (create/update)
  // NOTA: Al momento l'API supporta solo CREATE. UPDATE restituisce 403 Forbidden
  const handleSaveProject = async () => {
    setSaving(true);
    setFormError(null);
    
    if (!formData.name.trim()) {
      setFormError('Name is required.');
      setSaving(false);
      return;
    }

    if (!formData.customer_id) {
      setFormError('Customer is required.');
      setSaving(false);
      return;
    }

    if (!formData.project_code.trim()) {
      setFormError('Project code is required.');
      setSaving(false);
      return;
    }

    if (!formData.days_after_block || formData.days_after_block < 1) {
      setFormError('Days after block is required and must be greater than 0.');
      setSaving(false);
      return;
    }

    if (!formData.days_before_block || formData.days_before_block < 1) {
      setFormError('Days before block is required and must be greater than 0.');
      setSaving(false);
      return;
    }

    try {
      let payload, url, method;
      
      if (editingRow) {
        // Update - usa PUT come specificato nell'API
        payload = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          customer_id: parseInt(formData.customer_id),
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          active: formData.active,
          project_code: formData.project_code.trim(),
          days_after_block: parseInt(formData.days_after_block),
          days_before_block: parseInt(formData.days_before_block),
          ticket_id_prefix: formData.ticket_id_prefix.trim() || null,
          regex: formData.regex.trim() || null,
          regex_desc: formData.regex_desc.trim() || null,
          previous_month_blocked: formData.previous_month_blocked,
        };
        url = `${API_ENDPOINTS.PROJECTS}?id=${editingRow.id}`;
        method = 'PUT';
      } else {
        // Create - non includere l'ID
        payload = {
          name: formData.name.trim(),
          description: formData.description.trim() || null,
          customer_id: parseInt(formData.customer_id),
          start_date: formData.start_date || null,
          end_date: formData.end_date || null,
          active: formData.active,
          project_code: formData.project_code.trim(),
          days_after_block: parseInt(formData.days_after_block),
          days_before_block: parseInt(formData.days_before_block),
          ticket_id_prefix: formData.ticket_id_prefix.trim() || null,
          regex: formData.regex.trim() || null,
          regex_desc: formData.regex_desc.trim() || null,
          previous_month_blocked: formData.previous_month_blocked,
        };
        url = API_ENDPOINTS.PROJECTS;
        method = 'POST';
      }

      console.log(`[PROJECTS] ${method} request to:`, url);
      console.log('[PROJECTS] Payload:', payload);

      const response = await fetch(url, {
        method: method,
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('[PROJECTS] Response status:', response.status);
      
      if (!response.ok) {
        const err = await response.text();
        console.log('[PROJECTS] Error response:', err);
        
        // Se è un errore CORS, prova con approach alternativo
        if (err.includes('CORS') || response.status === 0) {
          console.log('[PROJECTS] Detected CORS issue, trying alternative approach...');
          throw new Error('CORS error detected. Please check server configuration.');
        }
        
        throw new Error(`${response.status}: ${err || 'Request failed'}`);
      }

      const result = await response.json();
      console.log('[PROJECTS] Update successful:', result);

      // Se è una creazione (non editing), aggiungi automaticamente l'utente come Service Manager
      if (!editingRow) {
        console.log('[PROJECTS] Create successful, adding user as Service Manager');
        
        try {
          // Estrai l'ID del progetto dalla risposta
          const projectId = result.id || result.project?.id;
          if (projectId) {
            console.log('[PROJECTS] Adding user as Service Manager for project ID:', projectId);
            
            // Crea l'associazione utente-progetto con ruolo Service Manager (project_role_id = 1)
            const userProjectPayload = {
              user_id: userId,
              project_role_id: 1 // Service Manager
            };
            
            const userProjectResponse = await fetch(`${API_ENDPOINTS.PROJECT_USERS}?project_id=${projectId}`, {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Accept': 'application/json'
              },
              body: JSON.stringify(userProjectPayload)
            });
            
            if (userProjectResponse.ok) {
              console.log('[PROJECTS] User successfully added as Service Manager');
            } else {
              const errorText = await userProjectResponse.text();
              console.error('[PROJECTS] Error adding user as Service Manager:', errorText);
              // Non bloccare il processo se l'aggiunta dell'utente fallisce
            }
          } else {
            console.error('[PROJECTS] Could not extract project ID from response:', result);
          }
        } catch (userProjectError) {
          console.error('[PROJECTS] Error adding user as Service Manager:', userProjectError);
          // Non bloccare il processo se l'aggiunta dell'utente fallisce
        }
      } else {
        console.log('[PROJECTS] Update successful for project ID:', editingRow.id);
      }

      setOpenDialog(false);
      setEditingRow(null);
      
      // Ricarica la lista
      fetchProjects();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>
            Projects Management
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            {canManageProjects() && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                color="primary"
                onClick={handleOpenDialog}
              >
                Add Project
              </Button>
            )}
          </Box>
        </Box>
        
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This is likely a backend API issue (HTTP 500). Please check the browser console for more details.
              </Typography>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={fetchProjects}
                sx={{ mr: 1 }}
              >
                🔄 Retry
              </Button>
              <Button 
                variant="text" 
                size="small" 
                onClick={() => console.log('API Endpoint:', API_ENDPOINTS.PROJECTS)}
              >
                Show Endpoint in Console
              </Button>
            </Box>
          </Alert>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              hideFooter={false}
              sx={{ bgcolor: 'white' }}
              disableSelectionOnClick
            />
          </Box>
        )}
      </Box>

      {/* Dialog per create/update project */}
        <Dialog 
          open={openDialog} 
          onClose={handleCloseDialog} 
          maxWidth="md" 
          fullWidth 
          scroll="paper" 
          sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
          PaperProps={{ sx: { mt: 6, mb: 6 } }}
        >
        <DialogTitle>
          {editingRow ? 'Edit Project' : 'New Project'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Prima riga: Nome e Codice */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="name"
                label="Project Name"
                value={formData.name}
                onChange={handleFormChange}
                required
                fullWidth
                error={!formData.name.trim() && formError}
                helperText={!formData.name.trim() && formError ? 'Name is required' : ''}
              />
              <TextField
                name="project_code"
                label="Project Code"
                value={formData.project_code}
                onChange={handleFormChange}
                required
                fullWidth
                error={!formData.project_code.trim() && formError}
                helperText={!formData.project_code.trim() && formError ? 'Project code is required' : ''}
              />
            </Box>
            
            {/* Seconda riga: Ticket ID Prefix */}
            <TextField
              name="ticket_id_prefix"
              label="Ticket ID Prefix"
              value={formData.ticket_id_prefix}
              onChange={handleFormChange}
              fullWidth
              placeholder="e.g., SAAAMSABH-, SAAMSELT-, TICKET-"
              helperText="Optional prefix for ticket IDs. When set, this prefix will be automatically added to ticket IDs for ticket resolution activities."
            />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                name="regex"
                label="Regex Pattern"
                value={formData.regex}
                onChange={handleFormChange}
                fullWidth
                placeholder="e.g., /^[A-Z]{2}[0-9]{4}$/i"
                helperText="Optional regex pattern for ticket ID validation. If ticket ID Prefix is valued, this regex will be used to validate the text after the prefix."
              />
              <Tooltip title="Click for regex help">
                <IconButton 
                  onClick={handleRegexHelpOpen}
                  size="small"
                  sx={{ mt: 1 }}
                >
                  <HelpIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <TextField
              name="regex_desc"
              label="Regex Description"
              value={formData.regex_desc}
              onChange={handleFormChange}
              fullWidth
              multiline
              rows={2}
              placeholder="e.g., Description shown to users when entering Ticket ID"
              helperText="This description will be shown to users when inserting/updating worklogs to explain the expected Ticket ID format."
            />
            
            {/* Terza riga: Customer */}
            <FormControl fullWidth required error={!formData.customer_id && formError}>
              <InputLabel>Customer</InputLabel>
              <Select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleFormChange}
                label="Customer"
                disabled={customersLoading}
              >
                {customersLoading ? (
                  <MenuItem value="">
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Loading customers...
                  </MenuItem>
                ) : (
                  customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
            
            {/* Quarta riga: Date */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                name="start_date"
                label="Start Date"
                type="date"
                value={formData.start_date}
                onChange={handleFormChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                name="end_date"
                label="End Date"
                type="date"
                value={formData.end_date}
                onChange={handleFormChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            {/* Quinta riga: Block Days */}
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                📅 <strong>Worklog Time Restrictions:</strong> These fields control how many days before and after the current date users are allowed to insert or update worklogs for this project.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  name="days_before_block"
                  label="Days Before Block"
                  type="number"
                  value={formData.days_before_block}
                  onChange={handleFormChange}
                  required
                  fullWidth
                  inputProps={{ min: 1 }}
                  error={!formData.days_before_block || formData.days_before_block < 1}
                  helperText={(!formData.days_before_block || formData.days_before_block < 1) 
                    ? 'Must be greater than 0' 
                    : 'Days before current date allowed for worklog entry'}
                />
                <TextField
                  name="days_after_block"
                  label="Days After Block"
                  type="number"
                  value={formData.days_after_block}
                  onChange={handleFormChange}
                  required
                  fullWidth
                  inputProps={{ min: 1 }}
                  error={!formData.days_after_block || formData.days_after_block < 1}
                  helperText={(!formData.days_after_block || formData.days_after_block < 1) 
                    ? 'Must be greater than 0' 
                    : 'Days after current date allowed for worklog entry'}
                />
              </Box>
              
              {/* Checkbox per previous_month_blocked */}
              <Box sx={{ mt: 2 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="previous_month_blocked"
                      checked={formData.previous_month_blocked}
                      onChange={handleFormChange}
                      color="primary"
                    />
                  }
                  label="Block previous month worklog entries"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                  When enabled, users cannot create or modify worklogs for the previous month
                </Typography>
              </Box>
              
            </Box>
            
            {/* Sesta riga: Descrizione */}
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleFormChange}
              multiline
              rows={3}
              fullWidth
            />
            
            {/* Settima riga: Status */}
            <FormControlLabel
              control={
                <Checkbox
                  name="active"
                  checked={formData.active}
                  onChange={handleFormChange}
                  color="primary"
                />
              }
              label="Active Project"
            />
            
            {formError && (
              <Alert severity="error">{formError}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveProject} 
            variant="contained"
            color="primary" 
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
        </Dialog>

        {/* Regex Help Dialog */}
        <Dialog 
          open={regexHelpOpen} 
          onClose={handleRegexHelpClose}
          maxWidth="md"
          fullWidth
          scroll="paper"
          sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
          PaperProps={{ sx: { mt: 6, mb: 6 } }}
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HelpIcon color="primary" />
              <Typography variant="h6">Regex Pattern Help</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              How to fill in the Ticket ID regex field
            </Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              A <strong>regex</strong> helps check that your Ticket IDs follow the correct pattern. If you don’t know how to write one, just use one of the examples below or change the letters/numbers to fit your needs.
            </Typography>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Ready-to-use examples:
            </Typography>
            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mb: 2 }}>
              <Box component="thead">
                <Box component="tr">
                  <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', py: 1 }}>Ticket Format</Box>
                  <Box component="th" sx={{ textAlign: 'left', borderBottom: '1px solid #e0e0e0', py: 1 }}>Use this regex</Box>
                </Box>
              </Box>
              <Box component="tbody">
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>ABC-123 (Jira)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^[A-Z]+-\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>12345 (Numbers only)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>INC0012345 (ServiceNow)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^INC\d+$</code></Box>
                </Box>
                <Box component="tr">
                  <Box component="td" sx={{ py: 1 }}>REQ45A (Letters+Numbers)</Box>
                  <Box component="td" sx={{ py: 1 }}><code>^[A-Za-z0-9]+$</code></Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              How to set exact or maximum length limits
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li>
                <Typography variant="body2"><strong>To require exactly 5 numbers</strong>: Use <code>^\d{'{'}5{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>12345</code>. Not valid: <code>1234</code>, <code>123456</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require between 3 and 6 letters</strong>: Use <code>^[A-Za-z]{'{'}3,6{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>ABC</code>, <code>abcd</code>, <code>AbCdEf</code>. Not valid: <code>AB</code>, <code>ABCDEFG</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require at most 4 digits</strong>: Use <code>^\d{'{'}1,4{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>1</code>, <code>1234</code>. Not valid: <code>12345</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>To require at least 3 digits</strong>: Use <code>^\d{'{'}3,{'}'}$</code></Typography>
                <Typography variant="body2">Example valid: <code>123</code>, <code>1234567</code></Typography>
              </li>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              What do the main symbols mean?
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              <li><Typography variant="body2"><code>^</code> → means “must start with”</Typography></li>
              <li><Typography variant="body2"><code>$</code> → means “must end with”</Typography></li>
              <li><Typography variant="body2"><code>[A-Z]</code> → one UPPERCASE letter</Typography></li>
              <li><Typography variant="body2"><code>[a-z]</code> → one lowercase letter</Typography></li>
              <li><Typography variant="body2"><code>\d</code> → a digit (0 to 9)</Typography></li>
              <li><Typography variant="body2"><code>+</code> → one or more of the previous character</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n{'}'}</code> → exactly n characters</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n,m{'}'}</code> → between n and m characters (inclusive)</Typography></li>
              <li><Typography variant="body2"><code>{'{'}n,{'}'}</code> → at least n characters</Typography></li>
            </Box>

            <Box sx={{ borderTop: '1px solid #e0e0e0', my: 2 }} />

            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Practical examples
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 1 }}>
              <li>
                <Typography variant="body2"><strong>Exactly 5 numbers ("12345")</strong>: Use <code>^\d{5}$</code></Typography>
                <Typography variant="body2">Valid: <code>12345</code>. Not valid: <code>1234</code>, <code>123456</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Numbers only, any length ("12345")</strong>: Use <code>^\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>1987</code>, <code>00234</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Jira ticket ("ABC-123")</strong>: Use <code>^[A-Z]+-\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>DEF-45</code>. Not valid: <code>def-45</code>, <code>DEF_45</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>ServiceNow ticket ("INC0012345")</strong>: Use <code>^INC\d+$</code></Typography>
                <Typography variant="body2">Valid: <code>INC123</code>. Not valid: <code>INC-123</code>, <code>INCabc</code></Typography>
              </li>
              <li>
                <Typography variant="body2"><strong>Letters and numbers mixed ("REQ45A")</strong>: Use <code>^[A-Za-z0-9]+$</code></Typography>
                <Typography variant="body2">Valid: <code>AB123C</code>. Not valid: <code>REQ 123</code>, <code>REQ-123</code></Typography>
              </li>
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Tip:</strong> Test your regex patterns using online tools like regex101.com or regexr.com before implementing them in your project.
              </Typography>
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleRegexHelpClose} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }